import { Ng2StateDeclaration, StatesModule } from '@uirouter/angular';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiAbstractStateComponent } from '@fi-shared/fi-state-proxy/fi-abstract-state.component';
import { Transition } from '@uirouter/core';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiChecklist2TemplateListComponent } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template-list/fi-checklist2-template-list.component';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiChecklist2EditComponent } from '@fi-shared/checklist/fi-checklist2-edit/fi-checklist2-edit.component';
import {
    ChecklistV2Edit_accessRightFilterCollection,
    ChecklistV2Edit_isChatAvailable,
    ChecklistV2Edit_savedQuickSelectionFiltersResolveFn,
} from '@fi-app/checklist/checklist-v2/checklist-v2.states';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChecklist2CheckpointsSectionEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoints-section-edit.component';
import {
    ChecklistV2Add_templateFn,
    ChecklistV2_EditCheckpointResolve,
    ChecklistV2Template_AddCheckpointSectionsResolve,
    ChecklistV2TemplateEdit_getTemplateFn,
    ChecklistV2_EditCheckpointSectionsResolve,
    ChecklistV2_templateCopyAddCheckpointSectionsResolve,
    ChecklistV2Add_templateCopyChecklistFn,
    onChecklistEditorStateExit,
} from '@fi-app/checklist/checklist-v2/checklist-v2-shared-resolvers';
import { FiCheckpointEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/checkpoint-edit.component';

const ChecklistV2TemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template._name,
    url: '/Template',
    abstract: true,
    component: FiAbstractStateComponent,
};

const ChecklistV2TemplateListState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiChecklist2TemplateListComponent,
    reloadOnSearch: false,
    resolve: {
        auditMode: [() => false],
    },
};

const accessRightFilterCollectionResolveFn = [
    AccessRightService,
    (accessRightService: AccessRightService) => {
        return accessRightService.getAccessRightsForPublicationByObjectType(ObjectTypes.Checklist2Template);
    },
];

const savedQuickSelectionFiltersResolveFn = [AccessRightService, ChecklistV2Edit_savedQuickSelectionFiltersResolveFn];

const ChecklistV2TemplateAddState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Add._name,
    url: '/Add',
    params: {
        checklistTemplate: null,
        isTemplateBased: false,
        templateMode: true,
    },
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    resolve: {
        checklist: ChecklistV2Add_templateFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: savedQuickSelectionFiltersResolveFn,
        templateMode: [() => true],
        isTemplateBased: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).isTemplateBased,
        ],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2TemplateAddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Add.AddCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: true,
        isTemplateBased: false,
        checkpoint: null,
    },
    resolve: ChecklistV2Template_AddCheckpointSectionsResolve,
};

const ChecklistV2TemplateAddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Add.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: true,
        isTemplateBased: false,
        checkpoint: null,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2TemplateEditCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Edit.EditCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: true,
        isTemplateBased: false,
    },
    resolve: ChecklistV2_EditCheckpointSectionsResolve,
};

const ChecklistV2TemplateEditCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Edit.EditCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: false,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2TemplateEditState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Edit._name,
    url: '/{id:[0-9]+}/Edit',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: false,
        isTemplateBased: false,
        templateMode: true,
    },
    resolve: {
        templateMode: [() => true],

        templateId: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => {
                return coerceNumberProperty(fiStateService.getParams(tr).id);
            },
        ],
        checklist: ChecklistV2TemplateEdit_getTemplateFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: savedQuickSelectionFiltersResolveFn,
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2FromTemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Copy._name,
    url: '/{id:[0-9]+}/Copy',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: false,
        isTemplateBased: true,
        templateMode: false,
    },
    resolve: {
        templateId: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => {
                return coerceNumberProperty(fiStateService.getParams(tr).id);
            },
        ],
        isTemplateBased: [() => true],
        accessRightFilterCollection: [AccessRightService, ChecklistV2Edit_accessRightFilterCollection],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        savedQuickSelectionFilters: savedQuickSelectionFiltersResolveFn,
        checklist: ChecklistV2Add_templateCopyChecklistFn,
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2FromTemplateAddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Copy.AddCheckpointSections._name,
    url: '/AddCheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: false,
        isTemplateBased: true,
    },
    resolve: ChecklistV2_templateCopyAddCheckpointSectionsResolve,
};

const ChecklistV2FromTemplateAddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Template.Copy.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: false,
        isTemplateBased: true,
        checkpoint: null,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

export const ChecklistV2TemplateStates: StatesModule = {
    states: [
        ChecklistV2TemplateState,
        ChecklistV2TemplateListState,
        ChecklistV2TemplateAddState,
        ChecklistV2TemplateEditState,
        ChecklistV2FromTemplateState,
        ChecklistV2FromTemplateAddCheckpointSectionsState,
        ChecklistV2FromTemplateAddCheckpointSectionsEditCheckpointState,
        ChecklistV2TemplateEditCheckpointSectionsState,
        ChecklistV2TemplateEditCheckpointSectionsEditCheckpointState,
        ChecklistV2TemplateAddCheckpointSectionsState,
        ChecklistV2TemplateAddCheckpointSectionsEditCheckpointState,
    ],
};
